import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import {
  Heart,
  FileDown,
  GitCompareArrows,
  FileText,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Loader2,
  Mail,
  X,
  ExternalLink,
} from 'lucide-react';
import { useProduct } from '../hooks/useProducts';
import { useAuth } from '../hooks/useAuth';
import { useFavoriteActions } from '../hooks/useFavoriteActions';
import { useCartActions } from '../hooks/useCartActions';
import { useCompareActions } from '../hooks/useCompareActions';
import { useRecentStore } from '../stores/useRecentStore';
import { useSettingsStore } from '../stores/useSettingsStore';
import { useTrackProductView } from '../hooks/useDashboard';
import Modal from '../components/common/Modal';
import Badge from '../components/common/Badge';
import Button from '../components/common/Button';
import SegmentControl from '../components/common/SegmentControl';
import NewBadge from '../components/products/NewBadge';
import NewStudyBadge from '../components/products/NewStudyBadge';
import MarketingClaimBadge from '../components/products/MarketingClaimBadge';
import TerritoryAgreementPill from '../components/products/TerritoryAgreementPill';
import FavoriteGroupPicker from '../components/common/FavoriteGroupPicker';
import CartPicker from '../components/common/CartPicker';
import { useProductPdfDownload } from '../hooks/usePdfDownload';
import { useTranslation } from '../i18n';
import EmailDialog from '../components/common/EmailDialog';
import type { EmailDialogSendData } from '../components/common/EmailDialog';
import { useProductEmail } from '../hooks/useProductEmail';
import { useOfflineStatus } from '../hooks/useOfflineStatus';
import { useToast } from '../context/ToastContext';
import HtmlContent from '../components/common/HtmlContent';
import HighlightedText from '../components/common/HighlightedText';
import { formatDose } from '../utils/formatDose';


function formatPrice(price: number): string {
  return `\u20AC${price.toFixed(2).replace('.', ',')}`;
}

type Tab = 'overview' | 'details' | 'more';

const ProductDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isOverlay = !!(location.state as { backgroundLocation?: Location })?.backgroundLocation;
  // Only set when the user arrived from a search result; a direct link carries no term.
  const searchTerm = (location.state as { searchTerm?: string } | null)?.searchTerm?.trim() ?? '';
  const { isLoggedIn, user } = useAuth();
  const { toggleFavorite, isFavorite } = useFavoriteActions(isLoggedIn);
  const { addToCart, removeFromCart, isInCart } = useCartActions(isLoggedIn);
  const { toggleCompare, isInCompare } = useCompareActions(isLoggedIn);
  const { addToRecentlyViewed } = useRecentStore();
  const { favoriteGroupsEnabled, multiCartEnabled } = useSettingsStore();
  const productPdf = useProductPdfDownload();
  const trackView = useTrackProductView();
  const productEmail = useProductEmail();
  const { isOnline } = useOfflineStatus();
  const toaster = useToast();

  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const { t: strings } = useTranslation();

  const TAB_OPTIONS = [
    { value: 'overview', label: strings.productDetail.tabs.overview },
    { value: 'details', label: strings.productDetail.tabs.productData },
    { value: 'more', label: strings.productDetail.tabs.moreDetails },
  ];

  const { data: product, isLoading, isError } = useProduct(id ?? '');

  const trackedId = useRef<string | null>(null);
  useEffect(() => {
    if (id && trackedId.current !== id) {
      trackedId.current = id;
      addToRecentlyViewed(id);
      trackView.mutate(id);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (toast === null) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleClose = useCallback(() => {
    if (isOverlay) {
      navigate(-1);
    } else {
      navigate('/products');
    }
  }, [navigate, isOverlay]);

  const handlePdf = useCallback(() => {
    if (product) productPdf.mutate({ product, strings, companyName: user?.branch_office?.name, companyWebsite: user?.branch_office?.website ?? undefined });
  }, [product, productPdf, strings, user?.branch_office?.name, user?.branch_office?.website]);

  const handleSendEmail = useCallback((data: EmailDialogSendData) => {
    if (!product) return;

    setEmailError(null);
    productEmail.mutate(
      {
        productId: product.id,
        product,
        recipients: data.recipients,
        subject: data.subject,
        body: data.body,
        strings,
        contactPerson: user?.name,
        companyName: user?.branch_office?.name,
        companyWebsite: user?.branch_office?.website ?? undefined,
      },
      {
        onSuccess: () => {
          setEmailOpen(false);
          setToast(strings.productDetail.toastEmail);
        },
        onError: (err) => {
          const apiErr = err as { body?: { errors?: Record<string, string[]> }; message?: string };
          if (apiErr.body?.errors) {
            const firstError = Object.values(apiErr.body.errors).flat()[0];
            setEmailError(firstError ?? strings.cart.email.sendError);
          } else {
            setEmailError(apiErr.message ?? strings.cart.email.sendError);
          }
        },
      },
    );
  }, [product, productEmail, strings, user?.name, user?.branch_office?.name, user?.branch_office?.website]);

  if (isLoading) {
    return (
      <Modal isOpen onClose={handleClose} wide>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Loader2 className="w-8 h-8 text-vivatis-400 animate-spin mb-4" />
          <p className="text-gray-500 dark:text-gray-400">Loading product...</p>
        </div>
      </Modal>
    );
  }

  if (!product || isError) {
    return (
      <Modal isOpen onClose={handleClose} wide>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-gray-600 dark:text-gray-400 font-medium mb-4">
            {isError ? 'Failed to load product' : 'Product not found'}
          </p>
          <Button variant="secondary" onClick={handleClose}>
            <ArrowLeft className="w-4 h-4" />
            Back to Overview
          </Button>
        </div>
      </Modal>
    );
  }

  const productId = String(product.id);
  const favorited = isFavorite(productId);
  const inCart = isInCart(product.id);
  const inCompare = isInCompare(productId);
  const dailyPrice = product.dailyDosePrice ? parseFloat(product.dailyDosePrice) : 0;

  /* ------- Side action bar (rendered outside modal panel) ------- */
  const sideActions = (
    <div className="flex flex-col gap-2 bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-2 w-[120px]">
      {favoriteGroupsEnabled ? (
        <FavoriteGroupPicker
          productId={productId}
          onSimpleToggle={() => toggleFavorite(productId)}
          size="md"
          direction="down"
        />
      ) : (
        <ActionIconButton
          icon={<Heart className={`w-5 h-5 ${favorited ? 'fill-red-500 text-red-500' : ''}`} />}
          label={favorited ? strings.actions.favorited : strings.actions.favorite}
          active={favorited}
          onClick={() => toggleFavorite(productId)}
        />
      )}
      <ActionIconButton
        icon={<FileDown className={`w-5 h-5 ${inCart ? 'text-vivatis-500' : ''}`} />}
        label={inCart ? strings.actions.inCartLabel : strings.actions.cartLabel}
        active={inCart}
        onClick={() => inCart ? removeFromCart(product.id) : addToCart(product.id)}
      />
      <ActionIconButton
        icon={<GitCompareArrows className={`w-5 h-5 ${inCompare ? 'text-vivatis-500' : ''}`} />}
        label={inCompare ? strings.actions.compared : strings.actions.compare}
        active={inCompare}
        onClick={() => toggleCompare(productId)}
      />
      <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
      <ActionIconButton
        icon={productPdf.isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
        label={productPdf.isPending ? 'Generating...' : 'PDF'}
        onClick={handlePdf}
      />
      <ActionIconButton
        icon={<Mail className="w-5 h-5" />}
        label={strings.productDetail.sendViaEmail}
        onClick={() => {
          if (!isOnline) {
            toaster.show(strings.common.offlineTooltip, 'warning');
            return;
          }
          setEmailOpen(true);
        }}
      />
    </div>
  );

  return (
    <>
      <Modal isOpen onClose={handleClose} wide sideContent={sideActions}>
        <div className="flex flex-col gap-5">
          {/* Toast banner */}
          {toast && (
            <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 rounded-full bg-green-600 px-5 py-3 text-white shadow-lg animate-fade-in">
              <Mail className="w-5 h-5 shrink-0" />
              <span className="text-sm font-medium">{toast}</span>
              <button
                type="button"
                className="ml-2 p-0.5 rounded hover:bg-green-700 transition-colors"
                onClick={() => setToast(null)}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {/* ---- Header ---- */}
        <div>
          <div className="flex items-start gap-3 flex-wrap pr-10">
            {product.logoUrl && (
              <img
                src={product.logoUrl}
                alt=""
                className="h-8 w-auto max-w-[8rem] object-contain shrink-0"
              />
            )}
            <h1 className="text-2xl font-bold text-anthracite-500 dark:text-gray-100 leading-tight">
              <HighlightedText text={product.name} term={searchTerm} />
            </h1>
            {product.isNew && <NewBadge />}
            {product.hasNewStudy && <NewStudyBadge />}
          </div>
          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500 dark:text-gray-400">
            <span className="font-mono"><HighlightedText text={product.articleNumber} term={searchTerm} /></span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            {product.partner?.logoUrl && (
              <img
                src={product.partner.logoUrl}
                alt=""
                className="h-4 w-auto max-w-[6rem] object-contain shrink-0"
              />
            )}
            <span><HighlightedText text={product.partner?.name} term={searchTerm} /></span>
          </div>
        </div>

        {/* ---- Tabs ---- */}
        <SegmentControl
          options={TAB_OPTIONS}
          value={activeTab}
          onChange={(v) => setActiveTab(v as Tab)}
        />

        {/* ---- Tab: Overview ---- */}
        {activeTab === 'overview' && (
          <div className="flex flex-col gap-5">
            {/* Pitch */}
            <div className="border-l-4 border-vivatis-400 bg-vivatis-50/60 dark:bg-vivatis-900/20 rounded-r-lg px-5 py-4">
              <HtmlContent
                html={product.pitch}
                highlightTerm={searchTerm}
                className="text-lg italic text-gray-800 dark:text-gray-200 leading-relaxed"
              />
            </div>

            {/* Short Description */}
            {product.shortDescription && (
              <section>
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1">
                  {strings.productDetail.shortDescription}
                </h2>
                <HtmlContent
                  html={product.shortDescription}
                  highlightTerm={searchTerm}
                  className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed"
                />
              </section>
            )}

            {/* Key Facts */}
            {(product.appearance || product.shelfLifeMonths != null || product.moq || product.bioactiveIngredients) && (
              <section>
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">
                  {strings.pdf.keyFacts}
                </h2>
                <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {product.appearance?.name && (
                    <DetailItem term={searchTerm} label={strings.pdf.appearance} value={product.appearance.name} />
                  )}
                  {product.shelfLifeMonths != null && (
                    <DetailItem term={searchTerm}
                      label={strings.pdf.shelfLife}
                      value={strings.pdf.shelfLifeValue.replace(':months', String(product.shelfLifeMonths))}
                    />
                  )}
                  {product.moq && <DetailItem term={searchTerm} label={strings.pdf.moq} value={product.moq} />}
                </dl>
                {product.bioactiveIngredients && (
                  <div className="mt-4">
                    <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      {strings.pdf.bioactiveIngredient}
                    </dt>
                    <HtmlContent
                      html={product.bioactiveIngredients}
                      highlightTerm={searchTerm}
                      className="mt-0.5 text-sm text-gray-800 dark:text-gray-200 leading-relaxed"
                    />
                  </div>
                )}
              </section>
            )}

            {/* Marketing Claims */}
            {product.marketingClaims.length > 0 && (
              <section>
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">
                  Marketing Claims
                </h2>
                <div className="flex flex-wrap gap-3">
                  {product.marketingClaims.map((claim) => (
                    <MarketingClaimBadge key={claim.id} claim={claim} showLabel />
                  ))}
                </div>
              </section>
            )}

            {/* Tags */}
            <div className="flex flex-col gap-3">
              {product.indications.length > 0 && (
                <TagRow label="Indications">
                  {product.indications.map((ind) => (
                    <Badge key={ind.id} variant="success"><HighlightedText text={ind.name} term={searchTerm} /></Badge>
                  ))}
                </TagRow>
              )}
              {product.applications.length > 0 && (
                <TagRow label="Applications">
                  {product.applications.map((app) => (
                    <Badge key={app.id} variant="info"><HighlightedText text={app.name} term={searchTerm} /></Badge>
                  ))}
                </TagRow>
              )}
              {product.territories.length > 0 && (
                <TagRow label="Countries">
                  {product.territories.map((territory) => (
                    <TerritoryAgreementPill key={territory.id} territory={territory} showName />
                  ))}
                </TagRow>
              )}
            </div>
          </div>
        )}

        {/* ---- Tab: Product Data ---- */}
        {activeTab === 'details' && (
          <div className="flex flex-col gap-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2 flex flex-col gap-5">
                <section>
                  <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1">
                    Description
                  </h2>
                  <HtmlContent
                    html={product.description}
                    highlightTerm={searchTerm}
                    className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed"
                  />
                </section>
                <section>
                  <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1">
                    Studies
                  </h2>
                  <HtmlContent
                    html={product.studies}
                    highlightTerm={searchTerm}
                    className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed"
                  />
                </section>
                <section>
                  <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1">
                    Origin
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    <HighlightedText text={product.origin?.name} term={searchTerm} />
                  </p>
                </section>
                {safeHttpUrl(product.websiteUrl) && (
                  <section>
                    <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1">
                      {strings.productDetail.website}
                    </h2>
                    <a
                      href={safeHttpUrl(product.websiteUrl)!}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-vivatis-600 dark:text-vivatis-400 hover:underline break-all"
                    >
                      <ExternalLink className="w-4 h-4 shrink-0" />
                      {product.websiteUrl}
                    </a>
                  </section>
                )}
                {safeHttpUrl(product.multimediaUrl) && (
                  <section>
                    <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1">
                      {strings.productDetail.multimedia}
                    </h2>
                    <a
                      href={safeHttpUrl(product.multimediaUrl)!}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-vivatis-600 dark:text-vivatis-400 hover:underline break-all"
                    >
                      <ExternalLink className="w-4 h-4 shrink-0" />
                      {product.multimediaUrl}
                    </a>
                  </section>
                )}
              </div>
              <div className="flex flex-col gap-4">
                <DetailItem term={searchTerm} label="Dosage" value={formatDose(product)} />
                <DetailItem term={searchTerm} label="Appearance" value={product.appearance?.name ?? ''} />
                <DetailItem term={searchTerm} label="Category" value={product.category?.name ?? ''} />
                <DetailItem term={searchTerm} label="Clinical Studies" value={String(product.clinicalStudies)} />
                {product.novelFoodStatus && (
                  <DetailItem term={searchTerm}
                    label="Novel Food"
                    value={
                      (strings.comparison.novelFoodStatuses as Record<string, string>)[product.novelFoodStatus]
                      ?? product.novelFoodStatus
                    }
                  />
                )}
                {product.shelfLifeMonths != null && (
                  <DetailItem term={searchTerm}
                    label={strings.pdf.shelfLife}
                    value={strings.pdf.shelfLifeValue.replace(':months', String(product.shelfLifeMonths))}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* ---- Tab: More Details ---- */}
        {activeTab === 'more' && (
          <div className="flex flex-col gap-5">
            <PriceSection price={dailyPrice} />
          </div>
        )}

        {/* ---- Mobile action buttons (hidden on md+ where side bar shows) ---- */}
        <div className="flex md:hidden flex-wrap items-center gap-2 border-t border-gray-200 dark:border-gray-700 pt-4">
          {favoriteGroupsEnabled ? (
            <FavoriteGroupPicker
              productId={productId}
              onSimpleToggle={() => toggleFavorite(productId)}
              direction="down"
            />
          ) : (
            <AnimatedMobileButton
              variant={favorited ? 'primary' : 'ghost'}
              icon={<Heart className={`w-4 h-4 ${favorited ? 'fill-white text-white' : ''}`} />}
              onClick={() => toggleFavorite(productId)}
              label={favorited ? strings.actions.favorited : strings.actions.favorite}
            />
          )}
          {multiCartEnabled ? (
            <CartPicker productId={product.id} direction="down" />
          ) : (
            <AnimatedMobileButton
              variant={inCart ? 'secondary' : 'primary'}
              icon={<FileDown className="w-4 h-4" />}
              onClick={() => inCart ? removeFromCart(product.id) : addToCart(product.id)}
              label={inCart ? strings.actions.inCartLabel : strings.actions.cartLabel}
            />
          )}
          <AnimatedMobileButton
            variant={inCompare ? 'secondary' : 'ghost'}
            icon={<GitCompareArrows className="w-4 h-4" />}
            onClick={() => toggleCompare(productId)}
            disabled={inCompare}
            label={inCompare ? strings.actions.compared : strings.actions.compare}
          />
          <Button
            variant="ghost"
            size="sm"
            icon={productPdf.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            onClick={handlePdf}
            disabled={productPdf.isPending}
          >
            {productPdf.isPending ? 'Generating...' : 'PDF'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            icon={<Mail className="w-4 h-4" />}
            onClick={() => {
              if (!isOnline) {
                toaster.show(strings.common.offlineTooltip, 'warning');
                return;
              }
              setEmailOpen(true);
            }}
          >
            {strings.productDetail.sendViaEmail}
          </Button>
        </div>
        </div>
      </Modal>
      <EmailDialog
        isOpen={emailOpen}
        onClose={() => setEmailOpen(false)}
        onSend={handleSendEmail}
        isSending={productEmail.isPending}
        defaultSubject={strings.productDetail.emailSubject(product.name, user?.branch_office?.name ?? 'Vivatis Pharma')}
        defaultBody={strings.productDetail.emailBody(product.name, user?.name ?? '', user?.branch_office?.name ?? 'Vivatis Pharma GmbH')}
        error={emailError}
      />
    </>
  );
};

/* ---- Sub-components ---- */

function ActionIconButton({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick: () => void;
}) {
  const [animating, setAnimating] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined!);

  const handleClick = () => {
    onClick();
    setAnimating(true);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setAnimating(false), 500);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`flex flex-col items-center gap-1 rounded-lg px-3 py-2.5 text-xs font-medium transition-all duration-200 cursor-pointer ${
        active
          ? 'bg-vivatis-50 text-vivatis-600 dark:bg-vivatis-950/40 dark:text-vivatis-400'
          : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200'
      }`}
      title={label}
    >
      <span
        className={`transition-transform duration-300 ${
          animating ? 'scale-130 animate-bounce-once' : ''
        }`}
        style={animating ? { animation: 'action-pop 0.4s ease-out' } : undefined}
      >
        {icon}
      </span>
      <span className="leading-none">{label}</span>
    </button>
  );
}

/**
 * Returns the URL only if it uses a safe http(s) scheme, otherwise null.
 * Prevents `javascript:`/`data:` URLs from becoming clickable links (XSS).
 */
function safeHttpUrl(url: string | null | undefined): string | null {
  return url && /^https?:\/\//i.test(url.trim()) ? url : null;
}

function DetailItem({ label, value, term = '' }: { label: string; value: string; term?: string }) {
  return (
    <div>
      <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">
        <HighlightedText text={value} term={term} />
      </dd>
    </div>
  );
}

function TagRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide w-28 shrink-0">
        {label}
      </span>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function AnimatedMobileButton({
  variant,
  icon,
  onClick,
  disabled,
  label,
}: {
  variant: 'primary' | 'secondary' | 'ghost';
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  label: string;
}) {
  const [animating, setAnimating] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined!);

  const handleClick = () => {
    onClick();
    setAnimating(true);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setAnimating(false), 500);
  };

  return (
    <Button
      variant={variant}
      size="sm"
      icon={icon}
      onClick={handleClick}
      disabled={disabled}
      className={animating ? 'animate-action-pop' : ''}
      style={animating ? { animation: 'action-pop 0.4s ease-out' } : undefined}
    >
      {label}
    </Button>
  );
}

function PriceSection({ price }: { price: number }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full px-4 py-3 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors rounded-lg cursor-pointer"
      >
        <span>Show daily price</span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && (
        <div className="px-4 pb-3 text-sm text-gray-700 dark:text-gray-300 font-medium">
          {formatPrice(price)} / day
        </div>
      )}
    </div>
  );
}

export default ProductDetailPage;
